import React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import {
  FieldWithId,
  GroupWithId,
  PropertyTemplatesWithIds,
  SectionWithId,
  TemplateWithIds,
} from './MetadataInterfaces';
import GroupList from './GroupList';
import SectionList from './SectionList';
import FieldList from './FieldList';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import GroupForm from './GroupForm';
import SectionForm from './SectionForm';
import AddIcon from '@mui/icons-material/Add';
import { v4 as uuidv4 } from 'uuid';
import FieldForm from './FieldForm';
import { CustomControlTypes } from '../../../constants/Control';
import { useMetadata } from '../../../contexts/MetadataContext';
import MoveFieldForm from './MoveFieldForm';
import MoveSectionForm from './MoveSectionForm';
import MoveGroupForm from './MoveGroupForm';
import { SelectionType } from './SelectionModeToggle';

interface TemplateFieldEditorProps {
  template: TemplateWithIds;
  selectedPropertyType: string;
  localTemplate: PropertyTemplatesWithIds;
  selectedGroup: GroupWithId | null;
  selectedSection: SectionWithId | null;
  onGroupClick: (group: GroupWithId) => void;
  onSectionClick: (section: SectionWithId) => void;
  onUpdateGroups: (updatedGroups: GroupWithId[]) => void;
  onUpdateSections: (updatedSections: SectionWithId[]) => void;
  onUpdateFields: (updatedFields: FieldWithId[]) => void;
  setSelectedGroup: (group: GroupWithId | null) => void;
  setSelectedSection: (section: SectionWithId | null) => void;
  setLocalTemplate: (template: PropertyTemplatesWithIds) => void;
  isSelectionMode: boolean;
  selectedItems: string[];
  onSelectionChange: (type: SelectionType, id: string) => void;
}

const TemplateFieldEditor: React.FC<TemplateFieldEditorProps> = ({
  template,
  selectedGroup,
  selectedSection,
  onGroupClick,
  onSectionClick,
  onUpdateGroups,
  selectedPropertyType,
  onUpdateSections,
  localTemplate,
  onUpdateFields,
  setSelectedGroup,
  setSelectedSection,
  setLocalTemplate,
  isSelectionMode,
  selectedItems,
  onSelectionChange,
}) => {
  const { showPanel, hidePanel } = useSidePanel();
  const { fields } = useMetadata();

  const handleAddGroup = () => {
    showPanel(
      'Add Group',
      <GroupForm
        onSave={(newGroup) => {
          const groupWithId: GroupWithId = {
            ...newGroup,
            id: uuidv4(),
            sections: [],
          };
          const updatedGroups = [...template.groups, groupWithId];
          onUpdateGroups(updatedGroups);
          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleMoveGroup = (group: GroupWithId) => {
    showPanel(
      'Move/Copy Group',
      <MoveGroupForm
        group={group}
        currentPropertyType={selectedPropertyType}
        propertyTemplates={localTemplate}
        onSave={(group, destPropertyType, isCopy) => {
          // Create a deep copy of the localTemplate
          const updatedTemplates = JSON.parse(JSON.stringify(localTemplate));

          if (!isCopy) {
            // Remove group from current property type if it's a move operation
            updatedTemplates[selectedPropertyType].groups = updatedTemplates[
              selectedPropertyType
            ].groups.filter((g: GroupWithId) => g.id !== group.id);
          }

          // Add group to destination property type
          const groupToAdd = isCopy
            ? {
                ...group,
                id: uuidv4(),
                sections: group.sections.map((s) => ({
                  ...s,
                  id: uuidv4(),
                  fields: s.fields.map((f) => ({ ...f, id: uuidv4() })),
                })),
              }
            : group;

          updatedTemplates[destPropertyType].groups = [
            ...updatedTemplates[destPropertyType].groups,
            groupToAdd,
          ];

          // Update the local state
          setLocalTemplate(updatedTemplates);

          // Update the current view if we're still in the same property type
          if (selectedPropertyType === destPropertyType || !isCopy) {
            const updatedGroups = updatedTemplates[selectedPropertyType].groups;
            onUpdateGroups(updatedGroups);

            // Update selectedGroup if it was moved or we're in the destination property type
            if (!isCopy || selectedPropertyType === destPropertyType) {
              const updatedSelectedGroup = updatedGroups.find(
                (g: GroupWithId) => g.id === group.id,
              );
              if (updatedSelectedGroup) {
                setSelectedGroup(updatedSelectedGroup);
              } else {
                setSelectedGroup(null);
                setSelectedSection(null);
              }
            }
          }

          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleMoveSection = (section: SectionWithId) => {
    showPanel(
      'Move/Copy Section',
      <MoveSectionForm
        section={section}
        currentPropertyType={selectedPropertyType}
        currentGroup={selectedGroup!}
        propertyTemplates={localTemplate}
        onSave={(section, destPropertyType, destGroupId, isCopy) => {
          // Create a deep copy of the localTemplate
          const updatedTemplates = JSON.parse(JSON.stringify(localTemplate));

          if (!isCopy) {
            // Remove section from current group if it's a move operation
            updatedTemplates[selectedPropertyType].groups = updatedTemplates[
              selectedPropertyType
            ].groups.map((g: GroupWithId) =>
              g.id === selectedGroup!.id
                ? {
                    ...g,
                    sections: g.sections.filter(
                      (s: SectionWithId) => s.id !== section.id,
                    ),
                  }
                : g,
            );
          }

          // Add section to destination group
          const sectionToAdd = isCopy
            ? {
                ...section,
                id: uuidv4(),
                fields: section.fields.map((f) => ({ ...f, id: uuidv4() })),
              }
            : section;
          updatedTemplates[destPropertyType].groups = updatedTemplates[
            destPropertyType
          ].groups.map((g: GroupWithId) =>
            g.id === destGroupId
              ? { ...g, sections: [...g.sections, sectionToAdd] }
              : g,
          );

          // Update the local state
          setLocalTemplate(updatedTemplates);

          // Update the current view
          const updatedGroups = updatedTemplates[selectedPropertyType].groups;
          onUpdateGroups(updatedGroups);

          // Update selectedGroup
          const updatedSelectedGroup = updatedGroups.find(
            (g: GroupWithId) => g.id === selectedGroup!.id,
          );
          if (updatedSelectedGroup) {
            setSelectedGroup(updatedSelectedGroup);
            setSelectedSection(null);
          }

          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleMoveField = (field: FieldWithId) => {
    showPanel(
      'Move/Copy Field',
      <MoveFieldForm
        field={field}
        currentPropertyType={selectedPropertyType}
        currentGroup={selectedGroup!}
        currentSection={selectedSection!}
        propertyTemplates={localTemplate}
        onSave={(
          field,
          destPropertyType,
          destGroupId,
          destSectionId,
          isCopy,
        ) => {
          // Create a deep copy of the localTemplate
          const updatedTemplates = JSON.parse(JSON.stringify(localTemplate));

          if (!isCopy) {
            // Remove field from current section if it's a move operation
            updatedTemplates[selectedPropertyType].groups = updatedTemplates[
              selectedPropertyType
            ].groups.map((g: GroupWithId) =>
              g.id === selectedGroup!.id
                ? {
                    ...g,
                    sections: g.sections.map((s: SectionWithId) =>
                      s.id === selectedSection!.id
                        ? {
                            ...s,
                            fields: s.fields.filter(
                              (f: FieldWithId) => f.id !== field.id,
                            ),
                          }
                        : s,
                    ),
                  }
                : g,
            );
          }

          // Add field to destination section
          const fieldToAdd = isCopy ? { ...field, id: uuidv4() } : field;
          updatedTemplates[destPropertyType].groups = updatedTemplates[
            destPropertyType
          ].groups.map((g: GroupWithId) =>
            g.id === destGroupId
              ? {
                  ...g,
                  sections: g.sections.map((s: SectionWithId) =>
                    s.id === destSectionId
                      ? { ...s, fields: [...s.fields, fieldToAdd] }
                      : s,
                  ),
                }
              : g,
          );

          // Update the local state
          setLocalTemplate(updatedTemplates);

          // Update the current view
          const updatedGroups = updatedTemplates[selectedPropertyType].groups;
          onUpdateGroups(updatedGroups);

          // Update selectedGroup and selectedSection
          const updatedSelectedGroup = updatedGroups.find(
            (g: GroupWithId) => g.id === selectedGroup!.id,
          );
          if (updatedSelectedGroup) {
            setSelectedGroup(updatedSelectedGroup);
            const updatedSelectedSection = updatedSelectedGroup.sections.find(
              (s: SectionWithId) => s.id === selectedSection!.id,
            );
            if (updatedSelectedSection) {
              setSelectedSection(updatedSelectedSection);
            }
          }

          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };
  const handleEditGroup = (group: GroupWithId) => {
    showPanel(
      'Edit Group',
      <GroupForm
        group={group}
        onSave={(updatedGroup) => {
          const updatedGroups = template.groups.map((g) =>
            g.id === group.id ? { ...g, ...updatedGroup } : g,
          );
          onUpdateGroups(updatedGroups);
          if (selectedGroup && selectedGroup.id === group.id) {
            setSelectedGroup({ ...selectedGroup, ...updatedGroup });
          }
          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleDeleteGroup = (groupId: string) => {
    const updatedGroups = template.groups.filter((g) => g.id !== groupId);
    onUpdateGroups(updatedGroups);
    if (selectedGroup && selectedGroup.id === groupId) {
      setSelectedGroup(null);
      setSelectedSection(null);
    }
  };

  const handleAddSection = () => {
    if (!selectedGroup) return;
    showPanel(
      'Add Section',
      <SectionForm
        onSave={(newSection) => {
          const sectionWithId: SectionWithId = {
            ...newSection,
            id: uuidv4(),
            fields: [],
          };
          const updatedSections = [...selectedGroup.sections, sectionWithId];
          const updatedGroups = template.groups.map((g) =>
            g.id === selectedGroup.id ? { ...g, sections: updatedSections } : g,
          );
          onUpdateGroups(updatedGroups);
          setSelectedGroup({ ...selectedGroup, sections: updatedSections });
          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleEditSection = (section: SectionWithId) => {
    showPanel(
      'Edit Section',
      <SectionForm
        section={section}
        onSave={(updatedSection) => {
          if (!selectedGroup) return;
          const updatedSections = selectedGroup.sections.map((s) =>
            s.id === section.id ? { ...s, ...updatedSection } : s,
          );
          const updatedGroups = template.groups.map((g) =>
            g.id === selectedGroup.id ? { ...g, sections: updatedSections } : g,
          );
          onUpdateGroups(updatedGroups);
          setSelectedGroup({ ...selectedGroup, sections: updatedSections });
          if (selectedSection && selectedSection.id === section.id) {
            setSelectedSection({ ...selectedSection, ...updatedSection });
          }
          hidePanel();
        }}
        onCancel={hidePanel}
      />,
    );
  };

  const handleDeleteSection = (sectionId: string) => {
    if (!selectedGroup) return;
    const updatedSections = selectedGroup.sections.filter(
      (s) => s.id !== sectionId,
    );
    const updatedGroups = template.groups.map((g) =>
      g.id === selectedGroup.id ? { ...g, sections: updatedSections } : g,
    );
    onUpdateGroups(updatedGroups);
    setSelectedGroup({ ...selectedGroup, sections: updatedSections });
    if (selectedSection && selectedSection.id === sectionId) {
      setSelectedSection(null);
    }
  };

  const handleAddField = (fieldId?: string) => {
    if (!selectedSection) return;
    showPanel(
      'Add Field',
      <FieldForm
        onSave={(newField) => {
          const fieldWithId: FieldWithId = { ...newField, id: uuidv4() };
          let updatedFields = [...selectedSection.fields];
          if (!fieldWithId) {
            updatedFields = [...updatedFields, fieldWithId];
          } else {
            const index = updatedFields.findIndex((f) => f.id === fieldId);
            if (index !== -1) {
              updatedFields.splice(index + 1, 0, fieldWithId);
            } else {
              updatedFields.push(fieldWithId);
            }
          }
          handleUpdateFields(updatedFields);
          hidePanel();
        }}
        onCancel={hidePanel}
        groupings={template.groupings || []}
        availableControls={CustomControlTypes}
        availableKeys={fields().map((f) => f.Key)}
      />,
      null,
      600,
    );
  };

  const handleEditField = (field: FieldWithId) => {
    if (!selectedSection) return;
    showPanel(
      'Edit Field',
      <FieldForm
        field={field}
        onSave={(updatedField) => {
          const updatedFields = selectedSection.fields.map((f) =>
            f.id === field.id ? { ...f, ...updatedField } : f,
          );
          handleUpdateFields(updatedFields);
          hidePanel();
        }}
        onCancel={hidePanel}
        groupings={template.groupings || []}
        availableControls={CustomControlTypes}
        availableKeys={fields().map((f) => f.Key)}
      />,
      null,
      600,
    );
  };

  const handleDeleteField = (fieldId: string) => {
    if (!selectedSection) return;
    const updatedFields = selectedSection.fields.filter(
      (f) => f.id !== fieldId,
    );
    handleUpdateFields(updatedFields);
  };

  const handleUpdateFields = (updatedFields: FieldWithId[]) => {
    if (!selectedGroup || !selectedSection) return;
    const updatedSections = selectedGroup.sections.map((s) =>
      s.id === selectedSection.id ? { ...s, fields: updatedFields } : s,
    );
    const updatedGroups = template.groups.map((g) =>
      g.id === selectedGroup.id ? { ...g, sections: updatedSections } : g,
    );
    onUpdateGroups(updatedGroups);
    setSelectedGroup({ ...selectedGroup, sections: updatedSections });
    setSelectedSection({ ...selectedSection, fields: updatedFields });
    onUpdateFields(updatedFields);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', display: 'flex' }}>
      {' '}
      {/* Adjust 200px as needed to account for any headers or other elements */}
      <Grid container spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
        <Grid
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Groups</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              variant="contained"
              color="primary"
              size="small"
            >
              Add Group
            </Button>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <GroupList
              groups={template.groups}
              selectedGroup={selectedGroup}
              onGroupClick={onGroupClick}
              onUpdateGroups={onUpdateGroups}
              onEditGroup={handleEditGroup}
              onDeleteGroup={handleDeleteGroup}
              onMoveGroup={handleMoveGroup}
              isSelectionMode={isSelectionMode}
              selectedItems={selectedItems}
              onSelectionChange={(id) => onSelectionChange('groups', id)}
            />
          </Box>
        </Grid>

        <Grid
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Sections</Typography>
            {selectedGroup && (
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddSection}
                variant="contained"
                color="primary"
                size="small"
              >
                Add Section
              </Button>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {selectedGroup && (
              <SectionList
                sections={selectedGroup.sections}
                selectedSection={selectedSection}
                onSectionClick={onSectionClick}
                onUpdateSections={(updatedSections) => {
                  const updatedGroups = template.groups.map((g) =>
                    g.id === selectedGroup.id
                      ? { ...g, sections: updatedSections }
                      : g,
                  );
                  onUpdateGroups(updatedGroups);
                  setSelectedGroup({
                    ...selectedGroup,
                    sections: updatedSections,
                  });
                }}
                onEditSection={handleEditSection}
                onDeleteSection={handleDeleteSection}
                onMoveSection={handleMoveSection}
                isSelectionMode={isSelectionMode}
                selectedItems={selectedItems}
                onSelectionChange={(id) => onSelectionChange('sections', id)}
              />
            )}
          </Box>
        </Grid>

        <Grid
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Fields</Typography>
            {selectedSection && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddField()}
                variant="contained"
                color="primary"
                size="small"
              >
                Add Field
              </Button>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {selectedSection && (
              <FieldList
                fields={selectedSection.fields}
                onUpdateFields={handleUpdateFields}
                onEditField={handleEditField}
                onDeleteField={handleDeleteField}
                onMoveField={handleMoveField}
                onAddField={handleAddField}
                isSelectionMode={isSelectionMode}
                selectedItems={selectedItems}
                onSelectionChange={(id) => onSelectionChange('fields', id)}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateFieldEditor;
